import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  ListItem,
  Autocomplete,
  IconButton,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import axios from "axios";

const NominatimBaseURL = "https://nominatim.openstreetmap.org/search";
const SearchOnMap = ({ currentPage, setCurrentLocation, mapRef }) => {
  const [query, setQuery] = useState(""); // Search query state

  const [suggestions, setSuggestions] = useState([]);

  const [isLatLngMode, setIsLatLngMode] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const fetchSuggestions = useCallback(async (value) => {
    if (value.length > 2) {
      const { data } = await axios.get(NominatimBaseURL, {
        params: {
          q: value,
          format: "json",
          addressdetails: 1,
          limit: 5,
        },
      });

      setSuggestions(data);
    } else {
      setSuggestions([]);
    }
  }, []);

  // Handle location selection

  const handleSelectLocation = (place) => {
    if (place) {
      const { lat, lon, boundingbox, display_name } = place;

      const newLocation = [parseFloat(lat), parseFloat(lon), display_name];

      setCurrentLocation(newLocation);

      let zoomLevel = 13; // Default zoom level

      if (boundingbox) {
        const latDiff = Math.abs(boundingbox[0] - boundingbox[1]);

        const lonDiff = Math.abs(boundingbox[2] - boundingbox[3]);

        // Adjust zoom level based on the size of the bounding box

        if (latDiff < 0.1 && lonDiff < 0.1) {
          zoomLevel = 15; // Zoom in for smaller areas like towns
        } else if (latDiff < 1 && lonDiff < 1) {
          zoomLevel = 12; // Medium zoom for cities
        } else {
          zoomLevel = 3; // Zoom out for larger areas like countries
        }
      }

      if (currentPage === "Map" && mapRef.current) {
        mapRef.current.setView(newLocation, zoomLevel);
      }

      setQuery(place.display_name);
    }
  };

  useEffect(() => {
    if (query === "" && currentPage === "Home") {
      setCurrentLocation(null);
    }
  }, [query]);

  // Handle search button click or Enter key press

  const handleSearch = async () => {
    if (query) {
      const { data } = await axios.get(NominatimBaseURL, {
        params: {
          q: query,

          format: "json",

          addressdetails: 1,

          limit: 1,
        },
      });

      if (data.length > 0) {
        handleSelectLocation(data[0]);
      }
    }
  };

  const handleLatLngSubmit = async () => {
    if (latitude && longitude) {
      try {
        // Reverse geocode to get display name
        const response = await axios.get(
          "https://nominatim.openstreetmap.org/reverse",
          {
            params: {
              format: "json",
              lat: latitude,
              lon: longitude,
            },
          }
        );
        console.log(response.data);

        const display_name =
          response.data.display_name || `${latitude}, ${longitude}`;
        const newLocation = [
          parseFloat(latitude),
          parseFloat(longitude),
          display_name || `${latitude}, ${longitude}`,
        ];
        setCurrentLocation(newLocation);

        // if (currentPage === "Map" && mapRef.current) {
        //   mapRef.current.setView(
        //     [parseFloat(latitude), parseFloat(longitude)],
        //     13
        //   );
        // }
      } catch (error) {
        console.error("Error reverse geocoding:", error);
        // Fallback if reverse geocoding fails
        const newLocation = [
          parseFloat(latitude),
          parseFloat(longitude),
          `${latitude}, ${longitude}`,
        ];
        setCurrentLocation(newLocation);
      }
    }
  };

  return (
    <Box
      sx={{
        width: currentPage === "Home" ? "100%" : 300,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "relative",
      }}
    >
      {!isLatLngMode ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <Autocomplete
            freeSolo
            options={suggestions}
            getOptionLabel={(option) => option.display_name || ""}
            onInputChange={(_, newValue) => {
              setQuery(newValue);
              fetchSuggestions(newValue);
            }}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue === "object") {
                handleSelectLocation(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={
                  currentPage === "Home"
                    ? "Set Default Location"
                    : "Search location"
                }
                size={currentPage === "Home" ? "small" : "small"}
                variant="outlined"
                value={query}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <ListItem {...props} key={option.place_id}>
                {option.display_name}
              </ListItem>
            )}
            sx={{ flexGrow: 9 }}
          />
          <IconButton
            onClick={() => setIsLatLngMode(true)}
            sx={{
              position: "absolute",
              right: currentPage === "Map" ? "48px" : "-4px",
              top: "-30px",
              zIndex: 1,
            }}
          >
            <SwapHorizIcon fontSize="small" sx={{ color: "#fff" }} />
          </IconButton>
          {currentPage === "Map" && (
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                ml: 1,
                minWidth: "auto",
                width: "10%",
                p: 0,
              }}
            >
              <SearchIcon />
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ flexGrow: 9 }}>
            <TextField
              size="small"
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              sx={{ width: "50%" }}
            />
            <TextField
              size="small"
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              sx={{ width: "50%" }}
            />
          </Stack>
          <IconButton
            onClick={() => setIsLatLngMode(false)}
            sx={{
              position: "absolute",
              right: currentPage === "Map" ? "48px" : "-4px",
              top: "-30px",
              zIndex: 1,
            }}
          >
            <SwapHorizIcon fontSize="small" sx={{ color: "#fff" }} />
          </IconButton>
          {currentPage === "Home" && (
            <Button
              variant="contained"
              onClick={handleLatLngSubmit}
              sx={{
                ml: 1,
                minWidth: "auto",
                p: 1,
              }}
            >
              <SearchIcon />
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchOnMap;
